import { memo, useState } from "react";
import { getAvatarColor, getDisplayName, getInitials } from "../../function/CustomFunction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookmark, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

/**
 * Formats the last_update_time of a customer into a human-readable label.
 * Pure function — defined at module level so it's not recreated per render.
 */
function formatLastSeen(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMin = (now - date) / 60000;

  if (diffMin < 10) return "Now";

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const onlyDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (onlyDate.getTime() === today.getTime()) return "Today";
  if (onlyDate.getTime() === yesterday.getTime()) return "Yesterday";
  return date.toLocaleDateString();
}

/**
 * Customer — individual contact row in the sidebar list.
 *
 * Performance: wrapped in React.memo so it only re-renders when
 * the customer object or isActive flag changes.
 */
function Customer({ customer, isActive }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className={`customer-list-item border-bottom${isActive ? " customer-list-item--active" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={`Chat with ${getDisplayName(customer)}`}
    >
      <div className="d-flex align-items-center p-2 px-3">
        {/* Avatar */}
        <div className="position-relative me-3 flex-shrink-0">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center fw-bold shadow-sm"
            style={{
              height: "44px",
              width: "44px",
              fontSize: "16px",
              backgroundColor: getAvatarColor(customer),
              color: "#ffffff",
              overflow: "hidden",
              userSelect: "none",
            }}
          >
            {customer.profile_picture && customer.profile_picture !== "null" && customer.profile_picture !== "undefined" && !imgError ? (
              <img
                src={`https://api.campaigningtool.com/contact_picture/${customer.profile_picture}`}
                alt="avatar"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={() => setImgError(true)}
              />
            ) : (
              <span>{getInitials(customer)}</span>
            )}
          </div>
        </div>

        {/* Meta */}
        <div className="flex-grow-1 min-width-0">
          <div className="d-flex justify-content-between align-items-start">
            <div className="text-truncate">
              <span style={{ fontSize: "14px", fontWeight: 600 }}>
                {getDisplayName(customer)}
              </span>
              <FontAwesomeIcon
                icon={customer.is_saved_contact ? faBookmark : faExclamationTriangle}
                style={{
                  color: customer.is_saved_contact ? "#25D366" : "#FFC107",
                  fontSize: "11px",
                  marginLeft: "6px",
                }}
                title={customer.is_saved_contact ? "Saved contact" : "Unknown contact"}
              />
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <p className="mb-0 text-muted text-truncate" style={{ fontSize: "13px" }}>
              No message preview
            </p>
            <small className="text-muted flex-shrink-0 ms-2" style={{ fontSize: "11px" }}>
              {formatLastSeen(customer.receiver_last_reply_at || customer.last_update_time)}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(Customer);
